import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert } from '@mui/material'
import { makeRequest } from '../../../apis/makeRequest'
import {
    getTransports,
    getCandidateDocuments,
    searchDeliverySheets,
    getDeliverySheet,
    createDeliverySheet,
    updateDeliverySheet,
    cancelDeliverySheet,
    printDeliverySheet,
} from '../../../apis/backendRequests'
import { logisticaActions, notificationActions } from '../../../actions'
import {
    computeSeleccionTotales,
    getApiErrors,
    getApiErrorCode,
    getApiErrorMessage,
    getBlobApiErrorMessage,
    downloadFile,
    normalizePlanilla,
    plateNumbers,
    transportErrors,
    formatDateTime,
    documentKey,
    documentTypeOf,
    sinBultos,
    normalizeBultos,
    normalizeImporte,
    normalizeNote,
} from '../../../helpers/logisticaHelper'
import useDocumentLock from '../../../hooks/useDocumentLock'
import { HeaderContainer, MainContainer, MainTitle } from '../../../ui/StyledComponents'
import { useConfirm } from '../ConfirmDialog'
import PlanillaRepartoSelector from './PlanillaRepartoSelector'
import PlanillaRepartoTable from './PlanillaRepartoTable'
import HistorialLineas from './HistorialLineas'
import CamposObligatorios from './CamposObligatorios'
import BuscarPlanillaDialog from './BuscarPlanillaDialog'
import NotaDialog from './NotaDialog'
import PatenteDialog from '../Transportes/PatenteDialog'

const LOCK_TAKEN = 610
const SHEET_NOT_FOUND = 600
const SHEET_CANCELLED = 601
const DOCUMENT_TAKEN = 602

// Campos de la línea que la API espera tal como vinieron; el operador edita importe, dirección, bultos y nota
const toSaveLine = (d) => ({
    documentType: documentTypeOf(d),
    documentNumber: d.documentNumber,
    customerNumber: d.customerNumber,
    customerName: d.customerName,
    netAmount: normalizeImporte(d.netAmount),
    orderNumber: d.orderNumber,
    remitoNumber: d.remitoNumber,
    shippingMethod: d.shippingMethod,
    deliveryAddress: d.deliveryAddress,
    packages: normalizeBultos(d.packages),
    note: normalizeNote(d.note),
})

// Errores en los que la lista de transportes quedó vieja: no hay datos web del transporte o se desactivó
const TRANSPORT_STALE = [transportErrors.NOT_FOUND, transportErrors.INACTIVE]

export default function PlanillaReparto() {
    const dispatch = useDispatch()
    const {
        transports,
        cabecera,
        filtroDocs,
        documentos,
        selectedIds,
        historial,
        buscarFiltro,
        buscarResultados,
        dirty,
    } = useSelector((s) => s.logistica)
    const [buscarOpen, setBuscarOpen] = useState(false)
    const [lockMessage, setLockMessage] = useState('')
    // Al abrir una planilla se corta el lock un instante para que el hook lo vuelva a pedir
    const [lockPaused, setLockPaused] = useState(false)
    const [confirm, confirmDialog] = useConfirm()
    // Patente nueva que se guarda en el transporte antes de guardar la planilla: { transport, plate }
    const [patenteNueva, setPatenteNueva] = useState(null)
    const patenteNuevaResolveRef = useRef(null)
    // documentKey de la línea con el popup de nota abierto
    const [notaKey, setNotaKey] = useState(null)
    // Número de la última búsqueda de candidatas: una respuesta que llega después de otra búsqueda se descarta
    const candidatasReqRef = useRef(0)

    const isCancelled = cabecera.status === 'Cancelled'
    const lockSheetId = cabecera.sheetId && !isCancelled && !lockPaused ? cabecera.sheetId : null

    const showError = (message) => dispatch(notificationActions.showError({ message }))
    const showWarning = (message) => dispatch(notificationActions.showWarning({ message }))
    const showSuccess = (message) => dispatch(notificationActions.showSuccess({ message }))

    const handleLockLost = (error) => {
        if (error?.errorCode === LOCK_TAKEN) {
            setLockMessage(error.errorDescription)
            return
        }
        showWarning(`${error?.errorDescription ?? 'Se perdió el bloqueo de la planilla.'} Se vuelve a abrir la planilla.`)
        abrirPlanilla(cabecera.sheetId)
    }

    const { releaseLock, hasLock } = useDocumentLock(lockSheetId, handleLockLost)

    const readOnly = !!cabecera.sheetId && (isCancelled || !hasLock)

    // Los transportes activos, también los que todavía no tienen datos web: la patente se carga al elegirlos
    const cargarTransports = useCallback(() => {
        makeRequest(
            () => getTransports({ includeUnregistered: true }),
            (res) => dispatch(logisticaActions.setTransports(res.data)),
            null,
            { checkEmptyResults: false }
        )
    }, [dispatch])

    useEffect(() => {
        cargarTransports()
        return () => dispatch(logisticaActions.reset())
    }, [dispatch, cargarTransports])

    const showTransportError = (err) => {
        showError(getApiErrorMessage(err))
        if (getApiErrors(err).some((e) => TRANSPORT_STALE.includes(e.errorCode))) cargarTransports()
    }

    const selectedIdsSet = new Set(selectedIds)
    const totales = computeSeleccionTotales(documentos, selectedIdsSet)

    const notaDocumento = notaKey ? documentos.find((d) => documentKey(d) === notaKey) : null

    // Handlers estables para que las filas memorizadas no se redibujen todas en cada cambio.
    // Los de blur reciben el documento de la fila para no depender de la lista
    const handleToggleSelect = useCallback((key) => dispatch(logisticaActions.toggleSelected(key)), [dispatch])
    const handleEditImporte = useCallback(
        (key, value) => dispatch(logisticaActions.editDocumentoField(key, 'netAmount', value)),
        [dispatch]
    )
    const handleBlurImporte = useCallback((doc) => {
        const importe = normalizeImporte(doc.netAmount)
        if (importe !== doc.netAmount) dispatch(logisticaActions.editDocumentoField(documentKey(doc), 'netAmount', importe))
    }, [dispatch])
    const handleEditDireccion = useCallback(
        (key, value) => dispatch(logisticaActions.editDocumentoField(key, 'deliveryAddress', value)),
        [dispatch]
    )
    const handleEditBultos = useCallback(
        (key, value) => dispatch(logisticaActions.editDocumentoField(key, 'packages', value)),
        [dispatch]
    )
    const handleBlurBultos = useCallback((doc) => {
        const bultos = normalizeBultos(doc.packages)
        if (bultos !== doc.packages) dispatch(logisticaActions.editDocumentoField(documentKey(doc), 'packages', bultos))
    }, [dispatch])

    // Una nota que no cambió no marca la planilla como modificada: al guardarla la API reemplazaría la línea
    const handleNotaSave = (note) => {
        if (normalizeNote(note) !== normalizeNote(notaDocumento.note)) {
            dispatch(logisticaActions.editDocumentoField(notaKey, 'note', note))
        }
        setNotaKey(null)
    }

    const cargaPosteriorAReparto = !!cabecera.loadDate && !!cabecera.deliveryDate && cabecera.loadDate > cabecera.deliveryDate
    const canGuardar =
        !readOnly &&
        !!cabecera.deliveryDate &&
        !!cabecera.loadDate &&
        !cargaPosteriorAReparto &&
        !!cabecera.transportCode &&
        (cabecera.sheetId ? dirty : selectedIds.length > 0)

    const confirmarDescarte = async () =>
        !dirty ||
        confirm({
            title: 'Cambios sin guardar',
            message: 'La planilla tiene cambios sin guardar. ¿Descartarlos?',
            confirmText: 'Descartar',
            cancelText: 'Volver',
        })

    // Las candidatas dependen del transporte: sin transporte o sin rango solo quedan las líneas de la planilla.
    // Los candidatos tildados se conservan aunque el filtro no los traiga, salvo con keepSelected en false
    const buscarCandidatas = (transportCode = cabecera.transportCode, keepSelected = true) => {
        const { fechaDesde, fechaHasta, customer } = filtroDocs
        const reqId = ++candidatasReqRef.current
        if (!fechaDesde || !fechaHasta || !transportCode) {
            dispatch(logisticaActions.setCandidatas([], false))
            return
        }
        makeRequest(
            () => getCandidateDocuments(fechaDesde, fechaHasta, transportCode, customer?.customerNumber),
            (res) => {
                if (reqId === candidatasReqRef.current) dispatch(logisticaActions.setCandidatas(res.data, keepSelected))
            },
            (err) => {
                if (reqId === candidatasReqRef.current) showTransportError(err)
            },
            { checkEmptyResults: false }
        )
    }

    const handleFiltrar = () => {
        if (filtroDocs.fechaDesde > filtroDocs.fechaHasta) {
            showWarning('La fecha desde no puede ser posterior a la fecha hasta.')
            return
        }
        buscarCandidatas()
    }

    // Un transporte sin patentes abre el formulario para cargarla ahí mismo (Retira cliente no lleva patente).
    // Si no tiene datos web y se cancela, se deselecciona: la planilla y las candidatas los necesitan
    const handleTransportChange = async (transport) => {
        const transportCode = transport?.code ?? ''
        if (transportCode === cabecera.transportCode) return

        // Se guarda el transporte anterior antes del cambio para volver a él si el operador no confirma
        const anterior = { transportCode: cabecera.transportCode, plate: cabecera.plate }
        const dirtyAnterior = dirty
        const plates = plateNumbers(transport)
        dispatch(logisticaActions.setCabeceraFields({
            transportCode,
            plate: plates.length === 1 ? plates[0] : '',
        }))

        if (cabecera.sheetId || selectedIds.length > 0) {
            const ok = await confirm({
                title: 'Cambiar transporte',
                message: cabecera.sheetId
                    ? `Se van a descartar los documentos tildados que todavía no se guardaron en la planilla ${cabecera.sheetId}. ¿Cambiar de transporte?`
                    : 'Se van a descartar los documentos tildados de la planilla que estás creando. ¿Cambiar de transporte?',
                confirmText: 'Cambiar',
                cancelText: 'Volver',
            })
            if (!ok) {
                dispatch(logisticaActions.setCabeceraFields(anterior, dirtyAnterior))
                return
            }
            // Una búsqueda del transporte anterior que todavía no respondió ya no se aplica
            candidatasReqRef.current++
            dispatch(logisticaActions.descartarCandidatas())
        }

        if (transport && plates.length === 0 && !transport.isRetiraCliente) {
            const plate = await cargarPatenteNueva(transport, '')
            if (plate === null && !transport.isRegistered) {
                dispatch(logisticaActions.setCabeceraField('transportCode', ''))
                buscarCandidatas('')
                return
            }
        }
        buscarCandidatas(transport?.code)
    }

    const abrirPlanilla = async (sheetId) => {
        await releaseLock()
        setLockPaused(true)
        setLockMessage('')
        makeRequest(
            () => getDeliverySheet(sheetId),
            (res) => {
                const planilla = normalizePlanilla(res.data)
                dispatch(logisticaActions.loadPlanilla(planilla))
                setLockPaused(false)
                setBuscarOpen(false)
                if (planilla.status !== 'Cancelled') buscarCandidatas(planilla.transportCode)
            },
            (err) => {
                setLockPaused(false)
                showError(getApiErrorMessage(err))
                if (getApiErrorCode(err) === SHEET_NOT_FOUND) {
                    dispatch(logisticaActions.reset())
                    setBuscarOpen(true)
                }
            },
            { checkEmptyResults: false }
        )
    }

    const imprimir = (sheetId = cabecera.sheetId) => {
        const fileName = `PlanillaReparto_${sheetId}.pdf`
        makeRequest(
            () => printDeliverySheet(sheetId),
            async (res) => {
                // Un 200 que no es PDF trae el error en JSON
                if (!res.headers['content-type']?.includes('application/pdf')) {
                    showError(await getBlobApiErrorMessage({ response: res, message: 'No se pudo generar el PDF.' }))
                    return
                }
                downloadFile(new Blob([res.data], { type: 'application/pdf' }), fileName)
            },
            async (err) => showError(await getBlobApiErrorMessage(err)),
            { checkEmptyResults: false }
        )
    }

    const anular = () => {
        makeRequest(
            () => cancelDeliverySheet(cabecera.sheetId),
            (res) => {
                dispatch(logisticaActions.loadPlanilla(normalizePlanilla(res.data)))
                showSuccess(`Planilla ${cabecera.sheetId} anulada.`)
            },
            (err) => {
                showError(getApiErrorMessage(err))
                if (getApiErrorCode(err) === SHEET_CANCELLED) abrirPlanilla(cabecera.sheetId)
            },
            { checkEmptyResults: false }
        )
    }

    const handleAnular = async () => {
        const ok = await confirm({
            title: 'Anular planilla',
            message: `¿Anular la planilla ${cabecera.sheetId}? Los documentos quedan libres y la planilla no se puede volver a editar.`,
            confirmText: 'Anular',
            cancelText: 'Volver',
        })
        if (ok) anular()
    }

    // Abre el formulario de la patente; resuelve la patente guardada en el transporte, o null si se canceló
    const cargarPatenteNueva = (transport, plate) => new Promise((resolve) => {
        patenteNuevaResolveRef.current = resolve
        setPatenteNueva({ transport, plate })
    })

    const cerrarPatenteNueva = (savedPlate) => {
        patenteNuevaResolveRef.current?.(savedPlate)
        patenteNuevaResolveRef.current = null
        setPatenteNueva(null)
    }

    // Botón + de la patente: las patentes solo se crean desde el formulario, no se escriben en el campo
    const handleNuevaPatente = () => {
        const transport = transports.find((t) => t.code === cabecera.transportCode)
        if (transport) cargarPatenteNueva(transport, '')
    }

    const handlePatenteNuevaSaved = (transport, plate) => {
        dispatch(logisticaActions.setTransports(transports.map((t) => (t.code === transport.code ? transport : t))))
        // En el formulario se puede corregir la patente: la planilla lleva la que quedó guardada
        dispatch(logisticaActions.setCabeceraField('plate', plate))
        cerrarPatenteNueva(plate)
    }

    const handleGuardar = async () => {
        if (!cabecera.deliveryDate || !cabecera.loadDate) {
            showWarning('Completá la fecha de reparto y la fecha de carga.')
            return
        }
        if (cargaPosteriorAReparto) {
            showWarning('La fecha de carga no puede ser posterior a la fecha de reparto.')
            return
        }
        if (!cabecera.transportCode) {
            showWarning('Seleccioná un transporte.')
            return
        }

        const tildados = documentos.filter((d) => selectedIdsSet.has(documentKey(d)))
        const conBultosEnCero = tildados.filter(sinBultos).length
        if (conBultosEnCero > 0) {
            showWarning(
                conBultosEnCero === 1
                    ? 'No se puede guardar: hay 1 documento tildado con 0 bultos. Cargá sus bultos antes de guardar.'
                    : `No se puede guardar: hay ${conBultosEnCero} documentos tildados con 0 bultos. Cargá sus bultos antes de guardar.`
            )
            return
        }
        const lines = tildados.map(toSaveLine)

        if (lines.length === 0) {
            if (!cabecera.sheetId) {
                showWarning('Seleccioná al menos un documento para guardar la planilla.')
                return
            }
            const ok = await confirm({
                title: 'Planilla sin documentos',
                message: `La planilla ${cabecera.sheetId} no puede quedar sin documentos. ¿Querés anularla?`,
                confirmText: 'Anular',
                cancelText: 'Volver',
            })
            if (ok) anular()
            return
        }

        const payload = {
            deliveryDate: cabecera.deliveryDate,
            loadDate: cabecera.loadDate,
            transportCode: cabecera.transportCode,
            plate: cabecera.plate,
            lines,
        }

        makeRequest(
            () => (cabecera.sheetId ? updateDeliverySheet(cabecera.sheetId, payload) : createDeliverySheet(payload)),
            async (res) => {
                // La respuesta es la planilla completa: los lineId cambian al editar
                const planilla = normalizePlanilla(res.data)
                dispatch(logisticaActions.loadPlanilla(planilla))
                showSuccess(`Planilla ${planilla.sheetId} guardada.`)
                buscarCandidatas(planilla.transportCode)

                const imprimirAhora = await confirm({
                    title: 'Imprimir',
                    message: `¿Imprimir la planilla ${planilla.sheetId}?`,
                })
                if (imprimirAhora) imprimir(planilla.sheetId)
            },
            (err) => {
                showTransportError(err)
                if (getApiErrors(err).some((e) => e.errorCode === DOCUMENT_TAKEN)) buscarCandidatas(cabecera.transportCode, false)
                if (getApiErrorCode(err) === SHEET_CANCELLED) abrirPlanilla(cabecera.sheetId)
            },
            { checkEmptyResults: false }
        )
    }

    const handleLimpiar = async () => {
        if (!(await confirmarDescarte())) return
        await releaseLock()
        setLockMessage('')
        dispatch(logisticaActions.reset())
    }

    const handleBuscarFiltrar = () => {
        if (buscarFiltro.fechaDesde > buscarFiltro.fechaHasta) {
            showWarning('La fecha desde no puede ser posterior a la fecha hasta.')
            return
        }
        makeRequest(
            () => searchDeliverySheets(buscarFiltro.fechaDesde, buscarFiltro.fechaHasta, buscarFiltro.transportCode, buscarFiltro.documentNumber),
            (res) => dispatch(logisticaActions.setBuscarResultados(res.data)),
            (err) => showError(getApiErrorMessage(err)),
            { checkEmptyResults: false }
        )
    }

    const cancelledInfo = [
        cabecera.cancelledBy && `por ${cabecera.cancelledBy}`,
        cabecera.cancelledDate && `el ${formatDateTime(cabecera.cancelledDate)}`,
    ].filter(Boolean).join(' ')

    return (
        <MainContainer>
            <HeaderContainer>
                <MainTitle>Armado de planilla de reparto</MainTitle>
                {!cabecera.sheetId && (
                    <CamposObligatorios
                        campos={[
                            { label: 'Fecha de reparto', ok: !!cabecera.deliveryDate },
                            { label: 'Fecha de carga (no posterior al reparto)', ok: !!cabecera.loadDate && !cargaPosteriorAReparto },
                            { label: 'Transporte', ok: !!cabecera.transportCode },
                            { label: 'Al menos un documento tildado', ok: selectedIds.length > 0 },
                        ]}
                    />
                )}
            </HeaderContainer>

            {isCancelled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Planilla anulada{cancelledInfo ? ` ${cancelledInfo}` : ''}. Solo lectura.
                </Alert>
            )}
            {!isCancelled && cabecera.sheetId && !hasLock && lockMessage && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {lockMessage} La planilla se abre solo para ver.
                </Alert>
            )}

            <PlanillaRepartoSelector
                cabecera={cabecera}
                readOnly={readOnly}
                canGuardar={canGuardar}
                fechaCargaError={cargaPosteriorAReparto ? 'No puede ser posterior a la fecha de reparto' : ''}
                onCabeceraChange={(field, value) => dispatch(logisticaActions.setCabeceraField(field, value))}
                onTransportChange={handleTransportChange}
                onNuevaPatente={handleNuevaPatente}
                filtro={filtroDocs}
                onFiltroChange={(field, value) => dispatch(logisticaActions.setFiltroDocsField(field, value))}
                transports={transports}
                onFiltrar={handleFiltrar}
                onGuardar={handleGuardar}
                onLimpiar={handleLimpiar}
                onAnular={handleAnular}
                onBuscar={() => setBuscarOpen(true)}
            />

            <PlanillaRepartoTable
                documentos={documentos}
                selectedIds={selectedIdsSet}
                readOnly={readOnly}
                onToggleSelect={handleToggleSelect}
                onEditImporte={handleEditImporte}
                onBlurImporte={handleBlurImporte}
                onEditDireccion={handleEditDireccion}
                onEditBultos={handleEditBultos}
                onBlurBultos={handleBlurBultos}
                onOpenNota={setNotaKey}
                totales={totales}
                canImprimir={!!cabecera.sheetId && !isCancelled && !dirty}
                onImprimir={() => imprimir()}
            />

            {cabecera.sheetId && <HistorialLineas lineas={historial} />}

            <BuscarPlanillaDialog
                open={buscarOpen}
                onClose={() => setBuscarOpen(false)}
                filtro={buscarFiltro}
                onFiltroChange={(field, value) => dispatch(logisticaActions.setBuscarFiltroField(field, value))}
                transports={transports}
                onFiltrar={handleBuscarFiltrar}
                resultados={buscarResultados}
                onSelectPlanilla={async (planilla) => {
                    if (await confirmarDescarte()) abrirPlanilla(planilla.sheetId)
                }}
            />

            <NotaDialog
                open={!!notaDocumento}
                documento={notaDocumento}
                readOnly={readOnly || !selectedIdsSet.has(notaKey)}
                onClose={() => setNotaKey(null)}
                onSave={handleNotaSave}
            />

            <PatenteDialog
                open={!!patenteNueva}
                transport={patenteNueva?.transport}
                plate={null}
                initialPlate={patenteNueva?.plate ?? ''}
                onClose={() => cerrarPatenteNueva(null)}
                onSaved={handlePatenteNuevaSaved}
                onTransportNotFound={cargarTransports}
            />
            {confirmDialog}
        </MainContainer>
    )
}
